import React, { useState } from 'react';
import {
  Alert,
  Accordion,
  AccordionDetails,
  AccordionSummary,
  Box,
  Card,
  CardContent,
  Chip,
  Divider,
  Grid,
  LinearProgress,
  Menu,
  MenuItem,
  Paper,
  Stack,
  Button,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Typography,
} from '@mui/material';
import {
  Assessment,
  BarChart,
  CheckCircle,
  ExpandMore,
  Info,
  MonetizationOn,
  People,
  Speed,
  TrendingDown,
  TrendingFlat,
  TrendingUp,
  Warning,
} from '@mui/icons-material';
import { usePricingSuggestions } from '../hooks/usePricing';

type AlertSeverity = 'error' | 'warning' | 'info' | 'success';

const PRIORITY_COLOR: Record<string, AlertSeverity> = {
  critical: 'error',
  high: 'warning',
  medium: 'info',
  low: 'success',
};

const PRIORITY_ICON: Record<string, React.ReactNode> = {
  critical: <Warning />,
  high: <TrendingUp />,
  medium: <Info />,
  low: <CheckCircle />,
};

const PricingSuggestions: React.FC = () => {
  const [period, setPeriod] = useState(90);
  const [targetMargin, setTargetMargin] = useState(25);
  const [periodAnchorEl, setPeriodAnchorEl] = useState<null | HTMLElement>(null);
  const [marginAnchorEl, setMarginAnchorEl] = useState<null | HTMLElement>(null);

  const {
    data: suggestions,
    isLoading,
    isFetching,
    error,
  } = usePricingSuggestions({ period, target_margin: targetMargin });

  const marginColor = (margin: number) => {
    if (margin < 10) return '#f44336';
    if (margin < targetMargin) return '#ff9800';
    return '#4caf50';
  };

  const hasUrgentRecs = suggestions?.recommendations?.some(
    (r: any) => r.priority === 'critical' || r.priority === 'high'
  );

  const backendError: string | null = suggestions?.error ?? null;

  // Pre-compute metric values so JSX stays clean
  const margin: number = suggestions?.current_metrics?.current_margin_percentage ?? 0;
  const profit: number = suggestions?.current_metrics?.current_profit_per_crate ?? 0;
  const mColor = marginColor(margin);
  const pColor = profit < 0 ? '#f44336' : '#4caf50';
  const marginPct = Math.min(Math.max(margin, 0), 100);

  return (
    <Box>
      {/* Single loading indicator — covers both initial load and background refetch */}
      {(isLoading || isFetching) && (
        <LinearProgress
          sx={{
            position: 'fixed',
            top: 0,
            left: 0,
            right: 0,
            zIndex: 9999,
            height: 3,
            bgcolor: 'transparent',
            '& .MuiLinearProgress-bar': {
              background: 'linear-gradient(90deg, #ff9800 0%, #ffa726 100%)',
            },
          }}
        />
      )}

      {/* Toolbar */}
      <Paper
        sx={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          flexWrap: 'wrap',
          gap: 1.5,
          mb: 3,
          p: 2,
        }}
      >
        <Box>
          <Typography variant="h5" sx={{ color: '#ff9800', fontWeight: 700, lineHeight: 1.2 }}>
            Pricing Suggestions
          </Typography>
          {suggestions?.analysis_period && (
            <Typography variant="caption" color="text.secondary">
              {suggestions.analysis_period.start_date} → {suggestions.analysis_period.end_date} (
              {suggestions.analysis_period.days} days)
            </Typography>
          )}
        </Box>

        <Box sx={{ display: 'flex', gap: 1.5 }}>
          <Box>
            <Button
              onClick={(e) => setPeriodAnchorEl(e.currentTarget)}
              variant="outlined"
              sx={{
                minWidth: 140,
                color: 'text.primary',
                textTransform: 'none',
                borderColor: 'divider',
              }}
              disabled={isLoading}
            >
              Analysis: {period}d
            </Button>
            <Menu
              anchorEl={periodAnchorEl}
              open={Boolean(periodAnchorEl)}
              onClose={() => setPeriodAnchorEl(null)}
            >
              {[7, 30, 60, 90, 180, 365].map((d) => (
                <MenuItem
                  key={d}
                  selected={period === d}
                  onClick={() => {
                    setPeriod(d);
                    setPeriodAnchorEl(null);
                  }}
                >
                  {d} Days
                </MenuItem>
              ))}
            </Menu>
          </Box>

          <Box>
            <Button
              onClick={(e) => setMarginAnchorEl(e.currentTarget)}
              variant="outlined"
              sx={{
                minWidth: 140,
                color: 'text.primary',
                textTransform: 'none',
                borderColor: 'divider',
              }}
              disabled={isLoading}
            >
              Target: {targetMargin}%
            </Button>
            <Menu
              anchorEl={marginAnchorEl}
              open={Boolean(marginAnchorEl)}
              onClose={() => setMarginAnchorEl(null)}
            >
              {[15, 20, 25, 30, 35, 40].map((m) => (
                <MenuItem
                  key={m}
                  selected={targetMargin === m}
                  onClick={() => {
                    setTargetMargin(m);
                    setMarginAnchorEl(null);
                  }}
                >
                  {m}%
                </MenuItem>
              ))}
            </Menu>
          </Box>
        </Box>
      </Paper>

      {error && (
        <Alert severity="error" sx={{ mb: 3 }}>
          Failed to load pricing suggestions. Please try again later.
        </Alert>
      )}

      {backendError && (
        <Alert severity="warning" sx={{ mb: 3 }}>
          {backendError}
        </Alert>
      )}

      {!isLoading && !error && !suggestions && (
        <Alert severity="info" sx={{ mb: 3 }}>
          No pricing data available. Please ensure you have sales, expenses, and production data.
        </Alert>
      )}

      {suggestions && !backendError && (
        <Box>
          {/* Current Metrics */}
          <Grid container spacing={3} sx={{ mb: 3 }}>
            <Grid size={{ xs: 12, sm: 6, md: 3 }}>
              <Card>
                <CardContent>
                  <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
                    <MonetizationOn sx={{ color: '#ff9800', mr: 1 }} />
                    <Typography variant="body2" color="text.secondary">
                      Avg. Selling Price
                    </Typography>
                  </Box>
                  <Typography variant="h5" sx={{ fontWeight: 700 }}>
                    GH₵ {suggestions.current_metrics?.average_selling_price || '0.00'}
                  </Typography>
                </CardContent>
              </Card>
            </Grid>

            <Grid size={{ xs: 12, sm: 6, md: 3 }}>
              <Card>
                <CardContent>
                  <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
                    <Assessment sx={{ color: '#ff9800', mr: 1 }} />
                    <Typography variant="body2" color="text.secondary">
                      Cost Per Crate
                    </Typography>
                  </Box>
                  <Typography variant="h5" sx={{ fontWeight: 700 }}>
                    GH₵ {suggestions.current_metrics?.cost_per_crate || '0.00'}
                  </Typography>
                </CardContent>
              </Card>
            </Grid>

            <Grid size={{ xs: 12, sm: 6, md: 3 }}>
              <Card>
                <CardContent>
                  <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
                    <TrendingUp sx={{ color: mColor, mr: 1 }} />
                    <Typography variant="body2" color="text.secondary">
                      Current Margin
                    </Typography>
                  </Box>
                  <Typography variant="h5" sx={{ fontWeight: 700, color: mColor }}>
                    {margin.toFixed(1)}%
                  </Typography>
                  <LinearProgress
                    variant="determinate"
                    value={marginPct}
                    sx={{
                      mt: 1,
                      height: 4,
                      borderRadius: 2,
                      bgcolor: 'action.hover',
                      '& .MuiLinearProgress-bar': { bgcolor: mColor, borderRadius: 2 },
                    }}
                  />
                  {margin < targetMargin && (
                    <Typography variant="caption" sx={{ color: mColor, display: 'block', mt: 0.5 }}>
                      {margin < 10
                        ? 'Critical — below break-even'
                        : `Below ${targetMargin}% target`}
                    </Typography>
                  )}
                </CardContent>
              </Card>
            </Grid>

            <Grid size={{ xs: 12, sm: 6, md: 3 }}>
              <Card>
                <CardContent>
                  <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
                    <MonetizationOn sx={{ color: pColor, mr: 1 }} />
                    <Typography variant="body2" color="text.secondary">
                      Profit Per Crate
                    </Typography>
                  </Box>
                  <Typography variant="h5" sx={{ fontWeight: 700, color: pColor }}>
                    GH₵ {Number(profit).toFixed(2)}
                  </Typography>
                </CardContent>
              </Card>
            </Grid>
          </Grid>

          {/* Recommendations — auto-expanded when there are critical/high items */}
          {suggestions.recommendations && suggestions.recommendations.length > 0 && (
            <Accordion defaultExpanded={!!hasUrgentRecs} sx={{ mb: 3 }}>
              <AccordionSummary expandIcon={<ExpandMore />}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                  <Typography variant="h6" sx={{ fontWeight: 600 }}>
                    Recommendations
                  </Typography>
                  {hasUrgentRecs && <Chip label="Action needed" size="small" color="error" />}
                </Box>
              </AccordionSummary>
              <AccordionDetails sx={{ p: 0 }}>
                <Paper sx={{ p: 3 }}>
                  <Stack spacing={2}>
                    {suggestions.recommendations.map((rec: any, index: number) => {
                      const severity: AlertSeverity = PRIORITY_COLOR[rec.priority] ?? 'info';
                      return (
                        <Alert key={index} severity={severity} icon={PRIORITY_ICON[rec.priority]}>
                          <Box>
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 0.5 }}>
                              <Typography variant="subtitle2" sx={{ fontWeight: 600 }}>
                                {rec.title}
                              </Typography>
                              <Chip
                                label={rec.priority?.toUpperCase()}
                                size="small"
                                color={severity}
                              />
                            </Box>
                            <Typography variant="body2">{rec.message}</Typography>
                            {rec.impact && (
                              <Typography
                                variant="caption"
                                color="text.secondary"
                                sx={{ mt: 0.5, display: 'block' }}
                              >
                                Impact: {rec.impact}
                              </Typography>
                            )}
                          </Box>
                        </Alert>
                      );
                    })}
                  </Stack>
                </Paper>
              </AccordionDetails>
            </Accordion>
          )}

          {/* Suggested Pricing */}
          <Paper sx={{ p: 3, mb: 3 }}>
            <Typography variant="h6" gutterBottom sx={{ fontWeight: 600, mb: 2 }}>
              Suggested Pricing
            </Typography>
            <Grid container spacing={2}>
              <Grid size={{ xs: 12, md: 4 }}>
                <Card sx={{ bgcolor: 'rgba(255, 152, 0, 0.05)' }}>
                  <CardContent>
                    <Typography variant="body2" color="text.secondary" gutterBottom>
                      Minimum Viable Price
                    </Typography>
                    <Typography variant="h4" sx={{ fontWeight: 700, color: '#ff9800', mb: 1 }}>
                      GH₵{' '}
                      {suggestions.suggested_pricing?.minimum_viable_price?.price_per_crate ||
                        '0.00'}
                    </Typography>
                    <Typography variant="body2" color="text.secondary">
                      {suggestions.suggested_pricing?.minimum_viable_price?.margin_percentage}%
                      Margin
                    </Typography>
                    <Typography variant="caption" sx={{ mt: 1, display: 'block' }}>
                      {suggestions.suggested_pricing?.minimum_viable_price?.description}
                    </Typography>
                  </CardContent>
                </Card>
              </Grid>

              <Grid size={{ xs: 12, md: 4 }}>
                <Card sx={{ bgcolor: 'rgba(76, 175, 80, 0.05)', border: '2px solid #4caf50' }}>
                  <CardContent>
                    <Typography variant="body2" color="text.secondary" gutterBottom>
                      Recommended Base Price
                    </Typography>
                    <Typography variant="h4" sx={{ fontWeight: 700, color: '#4caf50', mb: 1 }}>
                      GH₵ {suggestions.suggested_pricing?.recommended_base_price?.price_per_crate}
                    </Typography>
                    <Typography variant="body2" color="text.secondary">
                      {suggestions.suggested_pricing?.recommended_base_price?.margin_percentage}%
                      Margin
                    </Typography>
                    <Typography variant="body2" sx={{ mt: 1, color: '#4caf50' }}>
                      Profit: GH₵{' '}
                      {suggestions.suggested_pricing?.recommended_base_price?.profit_per_crate}
                      /crate
                    </Typography>
                    <Typography variant="caption" sx={{ mt: 1, display: 'block' }}>
                      {suggestions.suggested_pricing?.recommended_base_price?.description}
                    </Typography>
                  </CardContent>
                </Card>
              </Grid>

              <Grid size={{ xs: 12, md: 4 }}>
                <Card sx={{ bgcolor: 'rgba(156, 39, 176, 0.05)' }}>
                  <CardContent>
                    <Typography variant="body2" color="text.secondary" gutterBottom>
                      Premium Price
                    </Typography>
                    <Typography variant="h4" sx={{ fontWeight: 700, color: '#9c27b0', mb: 1 }}>
                      GH₵ {suggestions.suggested_pricing?.premium_price?.price_per_crate}
                    </Typography>
                    <Typography variant="body2" color="text.secondary">
                      {suggestions.suggested_pricing?.premium_price?.margin_percentage}% Margin
                    </Typography>
                    <Typography variant="caption" sx={{ mt: 1, display: 'block' }}>
                      {suggestions.suggested_pricing?.premium_price?.description}
                    </Typography>
                  </CardContent>
                </Card>
              </Grid>
            </Grid>

            {suggestions.suggested_pricing?.volume_based_tiers && (
              <Box sx={{ mt: 3 }}>
                <Typography variant="subtitle1" gutterBottom sx={{ fontWeight: 600 }}>
                  Volume-Based Pricing Tiers
                </Typography>
                <TableContainer>
                  <Table size="small">
                    <TableHead>
                      <TableRow>
                        <TableCell>Quantity Range</TableCell>
                        <TableCell align="right">Suggested Price</TableCell>
                        <TableCell align="right">Margin</TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {suggestions.suggested_pricing.volume_based_tiers.map(
                        (tier: any, index: number) => (
                          <TableRow key={index}>
                            <TableCell>
                              {tier.min_crates} – {tier.max_crates ?? '∞'} crates
                            </TableCell>
                            <TableCell align="right">
                              GH₵ {tier.suggested_price?.toFixed(2)}
                            </TableCell>
                            <TableCell align="right">{tier.margin_percentage}%</TableCell>
                          </TableRow>
                        )
                      )}
                    </TableBody>
                  </Table>
                </TableContainer>
              </Box>
            )}
          </Paper>

          {/* Cost Breakdown */}
          {suggestions.cost_breakdown && (
            <Accordion sx={{ mb: 3 }}>
              <AccordionSummary expandIcon={<ExpandMore />}>
                <Typography variant="h6" sx={{ fontWeight: 600 }}>
                  Cost Breakdown
                </Typography>
              </AccordionSummary>
              <AccordionDetails>
                <Grid container spacing={2}>
                  <Grid size={12}>
                    <Typography variant="body2" color="text.secondary" gutterBottom>
                      Total Expenses: GH₵ {suggestions.cost_breakdown.total_expenses?.toFixed(2)} |{' '}
                      Total Crates: {suggestions.cost_breakdown.total_crates_produced}
                    </Typography>
                    <Divider sx={{ my: 2 }} />
                  </Grid>
                  {suggestions.cost_breakdown.by_category &&
                    Object.entries(suggestions.cost_breakdown.by_category).map(
                      ([category, data]: [string, any]) => (
                        <Grid size={{ xs: 12, sm: 6, md: 4 }} key={category}>
                          <Card>
                            <CardContent>
                              <Typography variant="body2" color="text.secondary">
                                {category}
                              </Typography>
                              <Typography variant="h6" sx={{ fontWeight: 600, my: 1 }}>
                                GH₵ {data.cost_per_crate?.toFixed(2)}/crate
                              </Typography>
                              <Box
                                sx={{
                                  display: 'flex',
                                  justifyContent: 'space-between',
                                  alignItems: 'center',
                                }}
                              >
                                <Typography variant="caption" color="text.secondary">
                                  Total: GH₵ {data.total_amount?.toFixed(2)}
                                </Typography>
                                <Chip
                                  label={`${data.percentage?.toFixed(1)}%`}
                                  size="small"
                                  color="primary"
                                />
                              </Box>
                            </CardContent>
                          </Card>
                        </Grid>
                      )
                    )}
                </Grid>
              </AccordionDetails>
            </Accordion>
          )}

          {/* Profitability Scenarios */}
          {suggestions.profitability_scenarios && (
            <Accordion sx={{ mb: 3 }}>
              <AccordionSummary expandIcon={<ExpandMore />}>
                <Typography variant="h6" sx={{ fontWeight: 600 }}>
                  Profitability Scenarios
                </Typography>
              </AccordionSummary>
              <AccordionDetails>
                <TableContainer>
                  <Table>
                    <TableHead>
                      <TableRow>
                        <TableCell>Price/Crate</TableCell>
                        <TableCell align="right">Margin</TableCell>
                        <TableCell align="right">Profit/Crate</TableCell>
                        <TableCell align="right">Profit/1 000 Crates</TableCell>
                        <TableCell align="right">Monthly Estimate</TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {suggestions.profitability_scenarios.map((scenario: any, index: number) => {
                        const recommendedPrice = parseFloat(
                          suggestions.suggested_pricing?.recommended_base_price?.price_per_crate ??
                            '0'
                        );
                        const isRecommended =
                          Math.abs(scenario.price_per_crate - recommendedPrice) < 0.01;
                        return (
                          <TableRow
                            key={index}
                            sx={{
                              bgcolor: isRecommended ? 'rgba(76, 175, 80, 0.1)' : 'transparent',
                            }}
                          >
                            <TableCell>
                              GH₵ {scenario.price_per_crate?.toFixed(2)}
                              {isRecommended && (
                                <Chip
                                  label="Recommended"
                                  size="small"
                                  color="success"
                                  sx={{ ml: 1 }}
                                />
                              )}
                            </TableCell>
                            <TableCell align="right">{scenario.margin_percentage}%</TableCell>
                            <TableCell align="right">
                              GH₵ {scenario.profit_per_crate?.toFixed(2)}
                            </TableCell>
                            <TableCell align="right">
                              GH₵ {scenario.profit_per_1000_crates?.toFixed(2)}
                            </TableCell>
                            <TableCell align="right">
                              GH₵ {scenario.monthly_profit_estimate?.toFixed(2)}
                            </TableCell>
                          </TableRow>
                        );
                      })}
                    </TableBody>
                  </Table>
                </TableContainer>
              </AccordionDetails>
            </Accordion>
          )}

          {/* Market Insights */}
          {suggestions.market_insights && (
            <Accordion>
              <AccordionSummary expandIcon={<ExpandMore />}>
                <Typography variant="h6" sx={{ fontWeight: 600 }}>
                  Market Insights
                </Typography>
              </AccordionSummary>
              <AccordionDetails>
                <Grid container spacing={3}>
                  {/* Price Trend */}
                  {suggestions.market_insights.price_trend &&
                    (() => {
                      const trend = suggestions.market_insights.price_trend;
                      const TrendIcon =
                        trend.direction === 'increasing'
                          ? TrendingUp
                          : trend.direction === 'decreasing'
                            ? TrendingDown
                            : TrendingFlat;
                      const trendColor =
                        trend.direction === 'increasing'
                          ? '#4caf50'
                          : trend.direction === 'decreasing'
                            ? '#f44336'
                            : '#9e9e9e';
                      return (
                        <Grid size={{ xs: 12, md: 6 }}>
                          <Card>
                            <CardContent>
                              <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                                <TrendIcon sx={{ color: trendColor, mr: 1 }} />
                                <Typography variant="subtitle1" sx={{ fontWeight: 600 }}>
                                  Price Trend:{' '}
                                  <Box
                                    component="span"
                                    sx={{ color: trendColor, textTransform: 'capitalize' }}
                                  >
                                    {trend.direction}
                                  </Box>
                                </Typography>
                              </Box>
                              <Typography variant="body2" color="text.secondary">
                                Change: GH₵ {trend.change_amount?.toFixed(2)} (
                                {trend.change_percentage?.toFixed(1)}%)
                              </Typography>
                              {trend.period_midpoint && (
                                <Typography
                                  variant="caption"
                                  color="text.secondary"
                                  sx={{ mt: 0.5, display: 'block' }}
                                >
                                  First half vs second half split at {trend.period_midpoint}
                                </Typography>
                              )}
                            </CardContent>
                          </Card>
                        </Grid>
                      );
                    })()}

                  {/* Sales Velocity */}
                  {suggestions.market_insights.sales_velocity && (
                    <Grid size={{ xs: 12, md: 6 }}>
                      <Card>
                        <CardContent>
                          <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                            <Speed sx={{ color: '#ff9800', mr: 1 }} />
                            <Typography variant="subtitle1" sx={{ fontWeight: 600 }}>
                              Sales Velocity
                            </Typography>
                          </Box>
                          <Typography variant="body2" color="text.secondary">
                            Avg. Daily Sales:{' '}
                            {suggestions.market_insights.sales_velocity.average_daily_sales} crates
                          </Typography>
                          <Typography variant="body2" color="text.secondary">
                            Total Sold:{' '}
                            {suggestions.market_insights.sales_velocity.total_crates_sold} crates in{' '}
                            {suggestions.market_insights.sales_velocity.total_transactions}{' '}
                            transactions
                          </Typography>
                        </CardContent>
                      </Card>
                    </Grid>
                  )}

                  {/* Volume Analysis */}
                  {suggestions.market_insights.volume_analysis &&
                    suggestions.market_insights.volume_analysis.length > 0 && (
                      <Grid size={{ xs: 12, md: 6 }}>
                        <Card>
                          <CardContent>
                            <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                              <BarChart sx={{ color: '#ff9800', mr: 1 }} />
                              <Typography variant="subtitle1" sx={{ fontWeight: 600 }}>
                                Order Size Mix
                              </Typography>
                            </Box>
                            <Stack spacing={1}>
                              {suggestions.market_insights.volume_analysis.map((v: any) => (
                                <Box
                                  key={v.category}
                                  sx={{
                                    display: 'flex',
                                    justifyContent: 'space-between',
                                    alignItems: 'center',
                                  }}
                                >
                                  <Typography variant="body2" sx={{ textTransform: 'capitalize' }}>
                                    {v.category} orders
                                  </Typography>
                                  <Box sx={{ display: 'flex', gap: 1, alignItems: 'center' }}>
                                    <Typography variant="caption" color="text.secondary">
                                      {v.transaction_count} txn · {v.total_volume} crates
                                    </Typography>
                                    <Chip
                                      label={`GH₵ ${v.average_price?.toFixed(2)}`}
                                      size="small"
                                    />
                                  </Box>
                                </Box>
                              ))}
                            </Stack>
                          </CardContent>
                        </Card>
                      </Grid>
                    )}

                  {/* Customer Segments */}
                  {suggestions.market_insights.customer_segments && (
                    <Grid size={{ xs: 12, md: 6 }}>
                      <Card>
                        <CardContent>
                          <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                            <People sx={{ color: '#2196f3', mr: 1 }} />
                            <Typography variant="subtitle1" sx={{ fontWeight: 600 }}>
                              Customer Segments
                            </Typography>
                          </Box>
                          <Stack spacing={1}>
                            <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                              <Typography variant="body2">High Value (≥ GH₵3 000):</Typography>
                              <Chip
                                label={suggestions.market_insights.customer_segments.high_value}
                                size="small"
                                color="success"
                              />
                            </Box>
                            <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                              <Typography variant="body2">Medium Value (≥ GH₵900):</Typography>
                              <Chip
                                label={suggestions.market_insights.customer_segments.medium_value}
                                size="small"
                                color="info"
                              />
                            </Box>
                            <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                              <Typography variant="body2">Low Value:</Typography>
                              <Chip
                                label={suggestions.market_insights.customer_segments.low_value}
                                size="small"
                                color="default"
                              />
                            </Box>
                          </Stack>
                        </CardContent>
                      </Card>
                    </Grid>
                  )}

                  {/* Payment Behavior */}
                  {suggestions.market_insights.payment_behavior && (
                    <Grid size={{ xs: 12, md: 6 }}>
                      <Card>
                        <CardContent>
                          <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                            <CheckCircle sx={{ color: '#4caf50', mr: 1 }} />
                            <Typography variant="subtitle1" sx={{ fontWeight: 600 }}>
                              Payment Behavior
                            </Typography>
                          </Box>
                          <Typography variant="body2" color="text.secondary">
                            Paid on Time:{' '}
                            {suggestions.market_insights.payment_behavior.paid_on_time_percentage?.toFixed(
                              1
                            )}
                            %
                          </Typography>
                        </CardContent>
                      </Card>
                    </Grid>
                  )}
                </Grid>
              </AccordionDetails>
            </Accordion>
          )}
        </Box>
      )}
    </Box>
  );
};

export default PricingSuggestions;
